import { useState } from "react";
import { X, Check } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import TimelineEntryItem from "./TimelineEntryItem";

/**
 * Modal "Seguimiento interno" — timeline auditable + textarea para añadir
 * un comentario técnico manual.
 *
 * Props:
 *  - repair: objeto reparación con `code` y `timeline[]`.
 *  - repairId: id de la reparación (para el PUT del comentario manual).
 *  - onClose: cerrar el modal.
 *  - onUpdated: callback con la reparación actualizada tras añadir comentario.
 */
export default function TimelineModal({ repair, repairId, onClose, onUpdated }) {
  const [text, setText] = useState("");
  const [isBilling, setIsBilling] = useState(false);
  const [isLab, setIsLab] = useState(false);
  const [saving, setSaving] = useState(false);
  // Más reciente arriba — no mutamos el array original.
  const timeline = [...(repair?.timeline || [])].reverse();

  const addComment = async () => {
    const txt = text.trim();
    if (!txt) return;
    setSaving(true);
    try {
      const r = await api.put(`/repairs/${repairId}`, {
        technical_comment: txt, comment_is_billing: isBilling, comment_is_laboratory: isLab,
      });
      onUpdated(r.data);
      setText("");
      setIsBilling(false);
      setIsLab(false);
      toast.success("Comentario añadido al seguimiento");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="timeline-modal-backdrop"
    >
      <div
        className="bg-white border border-[#111110] shadow-hard w-full max-w-3xl max-h-[90vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
        data-testid="timeline-modal"
      >
        <div className="flex items-center justify-between px-5 py-4 border-b border-[#E5E5E0]">
          <div>
            <div className="font-mono text-[10px] uppercase tracking-widest text-[#6E8B3D]">
              // Auditoría inalterable
            </div>
            <h3 className="font-display text-xl font-black mt-1">
              Seguimiento interno · {repair?.code}
            </h3>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-[#F7F7F4]" data-testid="timeline-modal-close">
            <X size={18} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-5 space-y-3" data-testid="timeline-modal-entries">
          {timeline.length === 0 && (
            <div className="text-sm text-[#525252] text-center py-10">
              Aún no hay registros en el seguimiento.
            </div>
          )}
          {timeline.map((t) => (
            <TimelineEntryItem key={`${t.at}-${t.action}-${t.by}`} entry={t} />
          ))}
        </div>

        <div className="border-t border-[#E5E5E0] p-5 bg-[#F7F7F4]">
          <label className="font-mono text-[10px] uppercase tracking-widest text-[#525252]">
            Añadir comentario técnico
          </label>
          <textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            rows={3}
            placeholder="Anotación interna visible sólo en el seguimiento..."
            data-testid="timeline-modal-comment-input"
            className="mt-1 w-full ds-card focus:border-[#6E8B3D] focus:ring-1 focus:ring-[#6E8B3D] outline-none px-3 py-2 text-sm rounded-none"
          />
          <div className="mt-2 flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-2" data-testid="timeline-comment-tags">
              <button type="button" className="tl-check billing" data-on={isBilling} onClick={() => setIsBilling((v) => !v)}
                aria-pressed={isBilling} data-testid="timeline-comment-check-billing">
                <span className="box">{isBilling && <Check size={10} className="text-white" />}</span> Facturación
              </button>
              <button type="button" className="tl-check lab" data-on={isLab} onClick={() => setIsLab((v) => !v)}
                aria-pressed={isLab} data-testid="timeline-comment-check-laboratory">
                <span className="box">{isLab && <Check size={10} className="text-white" />}</span> Laboratorio
              </button>
            </div>
            <button
              type="button"
              onClick={addComment}
              disabled={saving || !text.trim()}
              data-testid="timeline-modal-comment-save"
              className="inline-flex items-center gap-2 bg-[#111110] hover:bg-[#6E8B3D] text-white px-4 py-2 text-sm font-semibold disabled:opacity-50 transition-colors"
            >
              <Check size={14} />
              {saving ? "Guardando..." : "Añadir al seguimiento"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
